import { useEffect, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, X, Plus } from "lucide-react";
import { useContentStore, FREQUENCY_OPTIONS, type ContentProjectConfig, type PublishConfig } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";

const PLATFORMS = [
  { id: "twitter", label: "X / Twitter" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "devto", label: "Dev.to" },
  { id: "hashnode", label: "Hashnode" },
] as const;

export function ContentSettings() {
  const [, params] = useRoute("/projects/:id/content/settings");
  const [, navigate] = useLocation();
  const projectId = params?.id ? parseInt(params.id, 10) : null;

  const config = useContentStore((s) => projectId ? s.config[projectId] : undefined);
  const fetchConfig = useContentStore((s) => s.fetchConfig);
  const updateConfig = useContentStore((s) => s.updateConfig);
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [brandVoice, setBrandVoice] = useState("");
  const [forbiddenWords, setForbiddenWords] = useState<string[]>([]);
  const [newForbiddenWord, setNewForbiddenWord] = useState("");
  const [targetDomain, setTargetDomain] = useState("");
  const [blogUrl, setBlogUrl] = useState("");
  const [publishConfig, setPublishConfig] = useState<Partial<PublishConfig>>({});
  const [enabledPlatforms, setEnabledPlatforms] = useState<string[]>([]);
  const [defaultLeadTime, setDefaultLeadTime] = useState("5");
  const [defaultFrequency, setDefaultFrequency] = useState("2");
  const [keywordClusters, setKeywordClusters] = useState("");
  const [autoResearch, setAutoResearch] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    fetchConfig(projectId).then(() => setLoading(false)).catch(() => setLoading(false));
  }, [projectId, fetchConfig]);

  // Hydrate form from config
  useEffect(() => {
    if (!config) return;
    setBrandVoice(config.brand_voice || "");
    setEnabledPlatforms(config.publishing_platforms || []);

    const templates = (config.content_templates || {}) as Record<string, unknown>;
    setForbiddenWords((templates.forbidden_words as string[]) || []);
    setTargetDomain((templates.target_domain as string) || "");
    setBlogUrl((templates.blog_url as string) || "");
    setDefaultLeadTime(String((templates.default_lead_time as number) || 5));
    setDefaultFrequency(String((templates.default_frequency as number) || 2));
    setPublishConfig(config.publish_config || {});
    setKeywordClusters((config.keyword_clusters || []).join("\n"));
    setAutoResearch(config.research_schedule?.enabled || false);
  }, [config]);

  const addForbiddenWord = () => {
    const word = newForbiddenWord.trim();
    if (word && !forbiddenWords.includes(word)) {
      setForbiddenWords([...forbiddenWords, word]);
      setNewForbiddenWord("");
    }
  };

  const togglePlatform = (platformId: string) => {
    setEnabledPlatforms((prev) =>
      prev.includes(platformId) ? prev.filter((p) => p !== platformId) : [...prev, platformId]
    );
  };

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const clusters = keywordClusters
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await updateConfig(projectId, {
        brand_voice: brandVoice || null,
        publishing_platforms: enabledPlatforms,
        content_templates: {
          forbidden_words: forbiddenWords,
          target_domain: targetDomain,
          blog_url: blogUrl,
          default_lead_time: parseInt(defaultLeadTime),
          default_frequency: parseInt(defaultFrequency),
        },
        publish_config: publishConfig,
        keyword_clusters: clusters,
        research_schedule: {
          enabled: autoResearch,
          frequency: "monthly",
          max_keywords_per_run: 50,
        },
      } as Partial<ContentProjectConfig>);
      toast({ title: "Settings saved" });
    } catch {
      toast({ title: "Failed to save settings", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) return null;

  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-2xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/projects/${projectId}/content`)}
          aria-label="Back to content dashboard"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-semibold">Content Settings</h1>
      </div>

      {/* 1. Voice & Style */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Voice & Style</h2>

        <div className="space-y-2">
          <Label className="text-xs">Style Guide Prompt</Label>
          <Textarea
            placeholder="Describe your brand voice, tone, and style preferences..."
            value={brandVoice}
            onChange={(e) => setBrandVoice(e.target.value)}
            rows={4}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Forbidden Words</Label>
          <div className="flex flex-wrap gap-1 mb-2">
            {forbiddenWords.map((word) => (
              <span
                key={word}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-xs"
              >
                {word}
                <button onClick={() => setForbiddenWords(forbiddenWords.filter((w) => w !== word))} aria-label={`Remove forbidden word: ${word}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Add word..."
              value={newForbiddenWord}
              onChange={(e) => setNewForbiddenWord(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addForbiddenWord(); } }}
              className="h-8"
            />
            <Button variant="outline" size="sm" onClick={addForbiddenWord} aria-label="Add forbidden word">
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </section>

      {/* 2. SEO & Domain */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">SEO & Domain</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Target Domain</Label>
            <Input
              placeholder="example.com"
              value={targetDomain}
              onChange={(e) => setTargetDomain(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Blog URL</Label>
            <Input
              placeholder="https://example.com/blog"
              value={blogUrl}
              onChange={(e) => setBlogUrl(e.target.value)}
              className="h-8"
            />
          </div>
        </div>
      </section>

      {/* 3. Git Publishing */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Git Publishing</h2>

        <div className="space-y-2">
          <Label className="text-xs">Blog Repository</Label>
          <Input
            placeholder="owner/repo (e.g. colony-so/blog)"
            value={publishConfig.blog_repo || ""}
            onChange={(e) => setPublishConfig({ ...publishConfig, blog_repo: e.target.value })}
            className="h-8"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Blog Path</Label>
            <Input
              placeholder="src/content/blog"
              value={publishConfig.blog_path || ""}
              onChange={(e) => setPublishConfig({ ...publishConfig, blog_path: e.target.value })}
              className="h-8"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Target Branch</Label>
            <Input
              placeholder="main"
              value={publishConfig.target_branch || ""}
              onChange={(e) => setPublishConfig({ ...publishConfig, target_branch: e.target.value })}
              className="h-8"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Blog Base URL</Label>
          <Input
            placeholder="https://colony.so/blog"
            value={publishConfig.blog_base_url || ""}
            onChange={(e) => setPublishConfig({ ...publishConfig, blog_base_url: e.target.value })}
            className="h-8"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Publish Mode</Label>
          <Select
            value={publishConfig.publish_mode || "direct_push"}
            onValueChange={(v) => setPublishConfig({ ...publishConfig, publish_mode: v as "direct_push" | "pull_request" })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct_push">Direct Push (to branch, no review)</SelectItem>
              <SelectItem value="pull_request">Pull Request (creates PR for review)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Rebuild Webhook URL</Label>
          <Input
            placeholder="https://api.cloudflare.com/... (optional)"
            value={publishConfig.rebuild_webhook_url || ""}
            onChange={(e) => setPublishConfig({ ...publishConfig, rebuild_webhook_url: e.target.value })}
            className="h-8"
          />
          <p className="text-[10px] text-muted-foreground">
            Cloudflare Pages, Vercel, or Netlify deploy hook URL. Triggered after each publish.
          </p>
        </div>
      </section>

      {/* 4. Distribution */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Distribution</h2>

        <div className="space-y-3">
          {PLATFORMS.map((p) => (
            <div key={p.id} className="flex items-center justify-between">
              <Label className="text-sm font-normal">{p.label}</Label>
              <Switch
                checked={enabledPlatforms.includes(p.id)}
                onCheckedChange={() => togglePlatform(p.id)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* 5. Keyword Research */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Keyword Research</h2>

        <div className="space-y-2">
          <Label className="text-xs">Seed Keywords</Label>
          <Textarea
            placeholder="Enter seed keywords, one per line"
            value={keywordClusters}
            onChange={(e) => setKeywordClusters(e.target.value)}
            rows={4}
          />
          <p className="text-[10px] text-muted-foreground">
            One keyword per line. These are expanded via SERP related searches.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">Auto-Research</Label>
            <p className="text-[10px] text-muted-foreground">Monthly automated keyword research</p>
          </div>
          <Switch
            checked={autoResearch}
            onCheckedChange={setAutoResearch}
          />
        </div>
      </section>

      {/* 6. Scheduling */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Scheduling</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Default Pipeline Lead Time</Label>
            <Select value={defaultLeadTime} onValueChange={setDefaultLeadTime}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="5">5 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="10">10 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Default Publish Frequency</Label>
            <Select value={defaultFrequency} onValueChange={setDefaultFrequency}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Save */}
      <div className="pt-4 border-t border-border/40">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
